import type { BillingCycle, CycleProduct, PlanInterval, PlanPrice, PlanPrices } from '@/types';

/**
 * Subscription plans — which Dodo product each cycle is, and how to show a price.
 *
 * Dodo is the source of truth for what Pro costs. This file deliberately holds
 * no amount and no currency: the server looks the product up in Dodo
 * (`functions/_pricing.ts`), serves the result from `GET /api/subscription/plans`,
 * and checkout reads the very same lookup. Changing a price is a change in the
 * Dodo dashboard, never here.
 *
 * Deliberately separate from `currencies.ts` and from the service catalogue.
 * What QuotyLab charges for itself and what a plumber charges for a callout are
 * unrelated numbers that happen to both be money, and conflating them is how a
 * catalogue re-price ends up changing the subscription fee.
 */

/**
 * Which environment variable holds each cycle's product id. Test and live
 * dashboards issue different ids, so the id itself is configuration, never code.
 */
export const SUBSCRIPTION_PRODUCTS: Readonly<Record<BillingCycle, CycleProduct>> = {
  monthly: { dodoProductEnvKey: 'DODO_PRODUCT_PRO_MONTHLY' },
  yearly: { dodoProductEnvKey: 'DODO_PRODUCT_PRO_YEARLY' },
} as const;

/**
 * Where the subscription lives in the app: the Plan card in Settings.
 *
 * Upgrade prompts elsewhere navigate here instead of opening a checkout of
 * their own, so there is exactly one checkout — `UpgradeButton` in that card.
 * A magic link returns here too (`signInReturnUrl`), which is why the card is
 * named by a query parameter and not a fragment: Supabase replaces the fragment
 * of the return URL with its tokens, but keeps the query.
 */
export const SUBSCRIPTION_ANCHOR = 'subscription';
export const UPGRADE_DESTINATION = { pathname: '/app/settings', search: `?section=${SUBSCRIPTION_ANCHOR}` } as const;

/** Whether a location asks for the Plan card. It only ever scrolls; it never navigates. */
export const wantsPlanCard = (search: string): boolean =>
  new URLSearchParams(search).get('section') === SUBSCRIPTION_ANCHOR;

/** What each cycle recurs on — the word after the slash in "$10.99 / month". */
export const CYCLE_INTERVAL: Readonly<Record<BillingCycle, PlanInterval>> = {
  monthly: 'month',
  yearly: 'year',
} as const;

/**
 * How many digits a currency's smallest unit has — 2 for USD, 0 for JPY, 3 for
 * KWD. Dodo sends amounts in that unit, so dividing by a fixed 100 would show a
 * yen price a hundred times too small. `Intl` knows each currency's ISO 4217
 * exponent, which is the same unit Dodo means by "lowest denomination".
 */
function minorUnitDigits(currency: string): number {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency })
    .resolvedOptions().maximumFractionDigits ?? 2;
}

/**
 * Display helper — "$10.99", "$109.99". Formats an amount in the currency's
 * smallest unit, exactly as Dodo returns it. Pure display: nothing multiplies
 * back, so no rounding ever reaches a charge.
 */
export function formatSubscriptionAmount(amountMinor: number, currency: string): string {
  const digits = minorUnitDigits(currency);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency })
    .format(amountMinor / 10 ** digits);
}

export const formatPlanPrice = (price: PlanPrice): string =>
  formatSubscriptionAmount(price.amountMinor, price.currency);

/**
 * What the yearly price saves against twelve monthly payments, as a percent.
 * Computed from Dodo's current prices, so the badge moves when they do — and
 * says nothing at all if the two are in different currencies.
 */
export function yearlySavingPercent(prices: PlanPrices): number {
  if (prices.monthly.currency !== prices.yearly.currency) return 0;
  const twelveMonths = prices.monthly.amountMinor * 12;
  const yearly = prices.yearly.amountMinor;
  if (twelveMonths <= 0 || yearly >= twelveMonths) return 0;
  return Math.round(((twelveMonths - yearly) / twelveMonths) * 100);
}
